/**
 * PDF Preview Modal Component
 *
 * Modal for previewing PDF exports before downloading.
 * Uses browser's native PDF viewer via iframe for full toolbar support.
 * On mobile devices, shows a download prompt since iframe PDF viewing is not well supported.
 */

import React, { useMemo, useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";

import {
  ModalOverlay,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalActionButton,
} from "@/components/UI/ModalOverlay";

export interface PDFPreviewModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  pdfData: ArrayBuffer | null;
  title?: string;
  onDownload: () => void;
  isDownloading?: boolean;
}

export const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  isOpen,
  onClose,
  pdfData,
  title: _title,
  onDownload,
  isDownloading = false,
}) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Create blob URL for the PDF to display in iframe
  const pdfUrl = useMemo(() => {
    if (!pdfData) return null;
    const blob = new Blob([pdfData], { type: "application/pdf" });

    return URL.createObjectURL(blob);
  }, [pdfData]);

  // Clean up blob URL when modal closes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} scrollBehavior="inside" size="4xl" onClose={onClose}>
      <ModalHeader className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Icon icon="vscode-icons:file-type-pdf2" width={24} />
          <span>{t("PDF Preview")}</span>
        </div>
      </ModalHeader>
      <ModalBody className="flex-1 min-h-0 overflow-hidden">
        {!pdfUrl && (
          <div className="flex items-center justify-center h-full">
            <p className="text-default-500">{t("No PDF data available")}</p>
          </div>
        )}

        {/* Mobile: Show download prompt instead of iframe */}
        {pdfUrl && isMobile && (
          <div className="flex flex-col items-center justify-center h-full gap-6 p-4 text-center">
            <Icon className="text-default-300" icon="solar:document-bold" width={64} />
            <div className="space-y-2">
              <p className="text-default-700 font-medium">
                {t("PDF preview is not available on mobile")}
              </p>
              <p className="text-default-500 text-sm">
                {t("Download the PDF to view it in your device's PDF reader")}
              </p>
            </div>
            {/*
             * The same shared component as the desktop footer below, not a raw
             * Button. This one sits in ModalBody rather than a ModalFooter, so
             * `modalFooterConsistency.test.ts` cannot see it, and it drifted
             * from its own desktop twin for exactly that reason: it kept a
             * hand-copied `.btn-brand-primary` and got no spinner and no
             * `aria-busy` while the footer button had both.
             */}
            <ModalActionButton
              isDisabled={isDownloading}
              isPending={isDownloading}
              onPress={onDownload}
            >
              {!isDownloading && <Icon icon="solar:download-bold" width={20} />}{" "}
              {isDownloading ? t("Downloading...") : t("Download PDF")}
            </ModalActionButton>
          </div>
        )}

        {/* Desktop: Show PDF in iframe */}
        {pdfUrl && !isMobile && (
          <iframe
            className="w-full h-full border-0 rounded-lg bg-white"
            src={pdfUrl}
            title={t("PDF Preview")}
          />
        )}
      </ModalBody>
      {/* Footer with download button on desktop */}
      {pdfUrl && !isMobile && (
        <ModalFooter className="justify-end border-t border-divider">
          <ModalActionButton
            isDisabled={isDownloading}
            isPending={isDownloading}
            onPress={onDownload}
          >
            {!isDownloading && <Icon icon="solar:download-bold" width={20} />}{" "}
            {isDownloading ? t("Downloading...") : t("Download")}
          </ModalActionButton>
        </ModalFooter>
      )}
    </ModalOverlay>
  );
};

export default PDFPreviewModal;
